import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { Banknote, CheckCircle2, Clock, Hash } from "lucide-react";

export default function PayoutHistory() {
  const [payouts, setPayouts] = useState(null);
  useEffect(() => {
    api.get("/payouts/mine").then((r) => setPayouts(r.data)).catch(() => setPayouts([]));
  }, []);

  if (!payouts) return <InlineLoader label="Loading payout history" />;

  const released = payouts.filter((p) => p.status === "released");
  const totalNet = released.reduce((s, p) => s + (p.net_amount || 0), 0);
  const pending = payouts.filter((p) => ["under_review", "not_eligible"].includes(p.status)).length;

  return (
    <div className="mx-auto max-w-4xl" data-testid="payout-history-page">
      <Seo title="Payout History" />
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Payout History</h1>
      <p className="mt-1 text-sm text-ff-medium">A full record of every payout you've requested, with fees and net amounts.</p>

      {payouts.length === 0 ? (
        <div className="mt-8">
          <EmptyState icon={Banknote} title="No payouts yet"
            message="Request a payout from a live campaign and it will appear here."
            action={<Button asChild className="bg-ff-action text-white"><Link to="/organizer/payout-request">Request a payout</Link></Button>}
            testid="payout-history-empty" />
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Total received (net)" value={money(totalNet)} accent="text-ff-green" testid="ph-total-net" />
            <Stat label="Payouts released" value={released.length} testid="ph-released-count" />
            <Stat label="In review" value={pending} testid="ph-pending-count" />
          </div>

          <div className="mt-6 space-y-3" data-testid="payout-history-list">
            {payouts.map((p) => (
              <div key={p.id} className="rounded-xl border border-ff-border bg-white p-4 shadow-sm" data-testid={`ph-row-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-ff-blue">{money(p.amount)}</div>
                    <div className="truncate text-sm text-ff-dark">{p.campaign_title}</div>
                    <div className="mt-0.5 text-xs text-ff-medium">
                      Requested {p.requested_at ? new Date(p.requested_at).toLocaleDateString() : "—"}
                      {p.released_at && <> · Released {new Date(p.released_at).toLocaleDateString()}</>}
                    </div>
                  </div>
                  <StatusPill status={p.status} />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-ff-border pt-3 text-xs">
                  <span className="text-ff-medium">Fee {p.fee_pct ?? 0}%: <span className="font-semibold text-ff-red">− {money(p.fee_amount || 0)}</span></span>
                  <span className="text-ff-medium">Net: <span className="font-semibold text-ff-green">{money(p.net_amount != null ? p.net_amount : p.amount)}</span></span>
                  {p.is_pro_payout && <span className="rounded-full bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-extrabold text-[#92400E]">PRO RATE</span>}
                  {p.stripe_transfer_id && (
                    <span className="inline-flex items-center gap-1 text-ff-medium" data-testid={`ph-transfer-${p.id}`}>
                      <Hash className="h-3 w-3" /> {p.stripe_transfer_id}
                    </span>
                  )}
                </div>

                {p.status === "released" ? (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-ff-green"><CheckCircle2 className="h-3.5 w-3.5" /> Funds released via Stripe</div>
                ) : p.eligibility_reasons?.length > 0 ? (
                  <div className="mt-2 flex items-start gap-2 text-xs text-ff-amber"><Clock className="mt-0.5 h-3.5 w-3.5" /> {p.eligibility_reasons.join(" · ")}</div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "text-ff-blue", testid }) {
  return (
    <div className="rounded-xl border border-ff-border bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-wide text-ff-medium">{label}</div>
      <div className={`mt-1 font-heading text-2xl font-extrabold ${accent}`}>{value}</div>
    </div>
  );
}
